import { useState, useEffect } from 'react';
import { GOLD, EMBER, MUTE, FAINT, GHOST, MONO, BASE } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Label, Ic } from './shared';
import * as api from '../lib/api';

// ── Leaderboard ─────────────────────────────────────────────
export function LeaderboardScreen() {
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getLeaderboard().then(r => { setBoard(r.data.leaderboard || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const medals = ['', GOLD, '#C0C0C0', '#CD7F32'];

  return (
    <div data-testid="leaderboard-screen" className="dettio-screen">
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 20 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>COMMUNITY</Mo>
          <Dp style={{ fontSize: 22, fontWeight: 800, color: 'var(--light)' }}>Leaderboard</Dp>
        </div>
        {loading ? <Mo style={{ color: 'var(--mute)', textAlign: 'center', display: 'block' }}>Loading...</Mo> : (
          board.length === 0 ? (
            <div className="dettio-empty">
              <div style={{ fontSize: 40, marginBottom: 16 }}>
                <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="1.5"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg>
              </div>
              <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>No one here yet</Dp>
              <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6 }}>Complete challenges and log payments to climb the leaderboard. Check back soon!</Bd>
            </div>
          ) : board.map((u, i) => (
            <div key={u.user_id} data-testid={`lb-row-${i}`} className={`dettio-lb-row${u.is_me ? ' dettio-lb-row--me' : ''}`}>
              <div className="dettio-lb-rank" style={{ background: u.rank <= 3 ? `${medals[u.rank]}20` : FAINT }}>
                <Mo style={{ fontSize: 14, fontWeight: 800, color: u.rank <= 3 ? medals[u.rank] : MUTE }}>
                  {u.rank}
                </Mo>
              </div>
              <div className={`dettio-lb-avatar${u.is_me ? ' dettio-lb-avatar--me' : ''}`} style={{ background: u.is_me ? undefined : FAINT }}>
                <Mo style={{ fontSize: 14, fontWeight: 700, color: u.is_me ? BASE : MUTE }}>{(u.name || '?')[0]}</Mo>
              </div>
              <div className="dettio-flex-1">
                <Bd style={{ fontSize: 14, fontWeight: 600, color: u.is_me ? GOLD : 'var(--light)' }}>
                  {u.name} {u.is_me && <Mo style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 700, letterSpacing: 1 }}>(YOU)</Mo>}
                </Bd>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                  {u.pct_free}% free · {u.streak} mo streak
                </Mo>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>{u.points.toLocaleString()}</Mo>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', letterSpacing: 1 }}>PTS</Mo>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Friend Activity Feed ────────────────────────────────────
export function ActivityScreen() {
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    api.getFriendActivity().then(r => setActivity(r.data.activity || [])).catch(() => {});
  }, []);

  const typeIcons = { payment: Ic.check, milestone: Ic.flame, streak: Ic.flame, badge: Ic.star };
  const typeColors = { payment: GOLD, milestone: EMBER, streak: GOLD, badge: GOLD };

  const timeSince = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div data-testid="activity-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <Label>COMMUNITY ACTIVITY</Label>
        {activity.map((a, i) => {
          const iconFn = typeIcons[a.type] || Ic.check;
          const color = typeColors[a.type] || GOLD;
          return (
            <div key={a.id || i} data-testid={`activity-${i}`} className="dettio-row dettio-flex--gap-md">
              <div className="dettio-icon-box" style={{ background: `${color}12`, flexShrink: 0 }}>{iconFn(16, color)}</div>
              <div className="dettio-flex-1">
                <Bd style={{ fontSize: 13, color: 'var(--light)', lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 700 }}>{a.name}</span> {a.text}
                </Bd>
                <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', marginTop: 4 }}>{timeSince(a.created_at)}</Mo>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Points & Streaks ────────────────────────────────────────
export function PointsScreen() {
  const [data, setData] = useState({ total_points: 0, streak: 0, recent: [] });
  useEffect(() => {
    api.getPoints().then(r => setData(r.data)).catch(() => {});
  }, []);

  const actionLabels = {
    payment: 'Made a payment', extra_payment: 'Extra payment', badge_earned: 'Badge earned',
    challenge_done: 'Challenge completed', streak_week: 'Weekly streak', spending_improved: 'Spending improved',
    first_debt_killed: 'First debt killed!', roundup_applied: 'Round-up applied', login_daily: 'Daily login',
  };

  return (
    <div data-testid="points-screen" className="dettio-screen">
      <Horizon intensity={0.7} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: 2, display: 'block', marginBottom: 8 }}>YOUR PROGRESS</Mo>
          <Dp style={{ fontSize: 42, fontWeight: 800, color: 'var(--gold)' }}>{data.total_points.toLocaleString()}</Dp>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 4 }}>TOTAL POINTS</Mo>
          <div className="dettio-flex dettio-flex--center" style={{ justifyContent: 'center', gap: 24, marginTop: 16 }}>
            <div className="dettio-screen__center">
              <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--ember)' }}>{data.streak}</Dp>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1 }}>MONTH STREAK</Mo>
            </div>
          </div>
        </div>

        <div className="dettio-info-box dettio-info-box--surface" style={{ marginBottom: 16 }}>
          <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>HOW TO EARN</Mo>
          {[
            { action: 'payment', pts: 50 }, { action: 'extra_payment', pts: 100 },
            { action: 'challenge_done', pts: 25 }, { action: 'streak_week', pts: 150 },
            { action: 'spending_improved', pts: 200 }, { action: 'first_debt_killed', pts: 500 },
          ].map(a => (
            <div key={a.action} className="dettio-flex dettio-flex--between" style={{ padding: '6px 0' }}>
              <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block' }}>{actionLabels[a.action]}</Bd>
              <Mo style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>+{a.pts}</Mo>
            </div>
          ))}
        </div>

        {data.recent.length > 0 && <>
          <Label>RECENT EARNINGS</Label>
          {data.recent.map((p, i) => (
            <div key={i} className="dettio-flex dettio-flex--between dettio-row--tight">
              <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{p.description || actionLabels[p.action] || p.action}</Bd>
              <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)' }}>+{p.points}</Mo>
            </div>
          ))}
        </>}
      </div>
    </div>
  );
}

// ── Recurring Payments ──────────────────────────────────────
export function RecurringScreen({ debts }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ debt_id: '', amount: '', frequency: 'monthly', day_of_month: '1' });

  useEffect(() => {
    api.getRecurring().then(r => setItems(r.data.recurring || [])).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.debt_id || !form.amount) return;
    const debt = debts.find(d => d.id === form.debt_id);
    try {
      const res = await api.createRecurring({
        ...form,
        debt_name: debt?.name || '',
        amount: parseFloat(form.amount),
        day_of_month: parseInt(form.day_of_month),
      });
      setItems(p => [...p, res.data]);
      setAdding(false);
      setForm({ debt_id: '', amount: '', frequency: 'monthly', day_of_month: '1' });
    } catch (e) { console.error(e); }
  };

  const remove = async (id) => {
    try { await api.deleteRecurring(id); setItems(p => p.filter(i => i.id !== id)); } catch (e) { console.error(e); }
  };

  return (
    <div data-testid="recurring-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <Label>SCHEDULED PAYMENTS ({items.length})</Label>
        {items.map(r => (
          <div key={r.id} data-testid={`recurring-${r.id}`} className="dettio-row dettio-flex--gap-md">
            <div className="dettio-icon-box" style={{ background: `${GOLD}12` }}>
              {Ic.check(16, GOLD)}
            </div>
            <div className="dettio-flex-1">
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{r.debt_name}</Bd>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                ${r.amount} · {r.frequency} · {r.day_of_month}{['st','nd','rd'][r.day_of_month-1] || 'th'} of month
              </Mo>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`del-recurring-${r.id}`} className="dettio-btn-cancel">CANCEL</button>
          </div>
        ))}

        {adding ? (
          <div className="dettio-info-box dettio-info-box--surface" style={{ marginTop: 12 }}>
            <Mo className="dettio-form-label">TARGET ACCOUNT</Mo>
            <select value={form.debt_id} onChange={e => setForm({ ...form, debt_id: e.target.value })} data-testid="recurring-debt-select"
              className="dettio-input dettio-input--small">
              <option value="">Select account...</option>
              {debts.map(d => <option key={d.id} value={d.id}>{d.name} (${d.balance.toLocaleString()})</option>)}
            </select>
            <Mo className="dettio-form-label">AMOUNT</Mo>
            <input type="number" placeholder="$0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="recurring-amount" className="dettio-input dettio-input--small" />
            <div className="dettio-flex dettio-flex--gap-sm">
              <div className="dettio-flex-1">
                <Mo className="dettio-form-label">FREQUENCY</Mo>
                <select value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })} data-testid="recurring-freq"
                  className="dettio-input dettio-input--small">
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="dettio-flex-1">
                <Mo className="dettio-form-label">DAY</Mo>
                <input type="number" min="1" max="28" placeholder="1" value={form.day_of_month} onChange={e => setForm({ ...form, day_of_month: e.target.value })} data-testid="recurring-day" className="dettio-input dettio-input--small" />
              </div>
            </div>
            <Btn onClick={save} gold data-testid="save-recurring-btn">SCHEDULE PAYMENT</Btn>
            <button onClick={() => setAdding(false)} className="dettio-btn-text dettio-btn-text--ghost" style={{ marginTop: 8 }}>CANCEL</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} data-testid="add-recurring-btn" className="dettio-add-btn" style={{ marginTop: 12 }}>
            + SCHEDULE NEW PAYMENT
          </button>
        )}
      </div>
    </div>
  );
}

// ── Custom Strategy Builder ─────────────────────────────────
export function CustomStrategyScreen({ debts, refreshData }) {
  const [order, setOrder] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Default order by balance (snowball)
    const sorted = [...debts].filter(d => d.status === 'active' || !d.status).sort((a, b) => a.balance - b.balance);
    setOrder(sorted.map(d => d.id));
  }, [debts]);

  const moveUp = (i) => {
    if (i === 0) return;
    const newOrder = [...order];
    [newOrder[i - 1], newOrder[i]] = [newOrder[i], newOrder[i - 1]];
    setOrder(newOrder);
    setSaved(false);
  };

  const moveDown = (i) => {
    if (i === order.length - 1) return;
    const newOrder = [...order];
    [newOrder[i], newOrder[i + 1]] = [newOrder[i + 1], newOrder[i]];
    setOrder(newOrder);
    setSaved(false);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await api.setCustomOrder(order);
      if (refreshData) await refreshData();
      setSaved(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const debtMap = {};
  debts.forEach(d => { debtMap[d.id] = d; });

  return (
    <div data-testid="custom-strategy-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 20 }}>
          <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)' }}>Custom Payoff Order</Dp>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', marginTop: 4 }}>Drag to reorder. Account #1 gets attacked first.</Bd>
        </div>
        {order.map((id, i) => {
          const d = debtMap[id];
          if (!d) return null;
          return (
            <div key={id} data-testid={`strategy-item-${i}`} className={`dettio-order-item${i === 0 ? ' dettio-order-item--target' : ''}`}>
              <div className="dettio-order-controls">
                <button onClick={() => moveUp(i)} data-testid={`move-up-${i}`} className={`dettio-order-btn${i === 0 ? ' dettio-order-btn--disabled' : ''}`}>^</button>
                <button onClick={() => moveDown(i)} data-testid={`move-down-${i}`} className={`dettio-order-btn${i === order.length - 1 ? ' dettio-order-btn--disabled' : ''}`}>v</button>
              </div>
              <Mo style={{ fontSize: 14, fontWeight: 800, color: i === 0 ? GOLD : MUTE, width: 24, textAlign: 'center' }}>{i + 1}</Mo>
              <div className="dettio-flex-1">
                <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{d.name}</Bd>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{d.apr}% APR · ${d.balance.toLocaleString()}</Mo>
              </div>
              {i === 0 && <Mo className="dettio-badge dettio-badge--gold">TARGET</Mo>}
            </div>
          );
        })}
        <div style={{ marginTop: 16 }}>
          <Btn onClick={saveOrder} gold disabled={saving} data-testid="save-custom-order-btn">
            {saved ? 'SAVED!' : saving ? 'SAVING...' : 'SET THIS ORDER'}
          </Btn>
        </div>

        <div style={{ marginTop: 16, padding: 12, background: 'var(--faint)' }}>
          <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>QUICK PRESETS</Mo>
          {[
            { label: 'SNOWBALL', desc: 'Smallest balance first', sort: (a, b) => a.balance - b.balance },
            { label: 'AVALANCHE', desc: 'Highest APR first', sort: (a, b) => b.apr - a.apr },
            { label: 'HYBRID', desc: 'Balance/APR ratio', sort: (a, b) => (a.balance / Math.max(a.apr, 0.01)) - (b.balance / Math.max(b.apr, 0.01)) },
          ].map(p => (
            <button key={p.label} onClick={() => { setOrder([...debts].sort(p.sort).map(d => d.id)); setSaved(false); }}
              data-testid={`preset-${p.label.toLowerCase()}`}
              className="dettio-preset-btn">
              {p.label} <span style={{ color: 'var(--ghost)', marginLeft: 8 }}>{p.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
